import * as React from "react";
import {cx}       from "emotion";

import "./import-target-surface.scss";

import {Link,
        makeChain} from "@Common/react/cst2";
import {Types}     from "@Common/types";


export const ImportTargetSurface = ({
    surface,
    onApplyImage,
    dragImageRef,
    dimmed        = false
}:{
    surface       : Types.Common.Tools.CcIntegration.Models.ProductSurfaceWithImages,
    onApplyImage  : Types.Common.UiComponents.PhotoDashboard.Functions.OnApplyImageForImport,
    dragImageRef  : Types.Common.React.UseRef.UseRefNext,
    dimmed       ?: boolean,
}) => (

    makeChain<JSX.Element>()

    // return empty container if no surface data provided
    .then((_, next:Link<JSX.Element>) => surface ? next() : (
        <div className="import-target-surface-k3e"></div>
    ))

    // re-order surface items from largest to smallest so that smaller items display on top of larger items - this
    // is meant to ensure that image areas are not hidden behind background images for some templates
    .then("imageAreas", (_, next:Link<JSX.Element, Types.Common.Tools.CcIntegration.Models.ProductImage[]>) => next(
        surface.items.sort(
            (x, y) => (x.width * x.height < y.width * y.height) ? 1 : -1
        )
    ))

    // build function for generating image pattern attributes
    .then("setSvgPatternImageAttributes", (_, next:Link<JSX.Element, (item:Types.Common.Tools.CcIntegration.Models.ProductImage) => Types.Common.UiComponents.PhotoDashboard.Models.SvgPatternAttributes>) => next(item => {

        // default the pattern attributes
        let svgAttributes:Types.Common.UiComponents.PhotoDashboard.Models.SvgPatternAttributes = {
            href: "",
            imageid: 0,
            imageprintpath: "",
            x: 0,
            y: 0,
            width: item.width,
            height: item.height,
        };

        // return defaults if no image set
        if (!item.image) return svgAttributes;

        // set the image location
        svgAttributes.href = item.image.httpPath;
        svgAttributes.imageid = item.image.imageId;
        svgAttributes.imageprintpath = item.image.httpPathPrint;

        // change attributes to center the image within the area

        // find aspect ratios of the image area and the image to be placed
        let imageAspect = item.image.width / item.image.height;
        let areaAspect = item.width / item.height;

        // trim sides where image is wider than area
        if (imageAspect > areaAspect) {
            svgAttributes.width = svgAttributes.height * imageAspect;
            svgAttributes.x = (svgAttributes.width - item.width) / -2;
        }
        // trim top/bottom where image is thinner than area
        else {
            svgAttributes.height = svgAttributes.width / imageAspect;
            svgAttributes.y = (svgAttributes.height - item.height) / -2;
        }

        return svgAttributes;

    }))

    // build function for generating an element ID including the surface name for surface uniqueness
    .then("getId", (_, next:Link<JSX.Element, (prefix : string, surfaceName : string) => string>) => next((prefix, surfaceName) => {
        return prefix + "-" + surfaceName.toLowerCase().replace(/ /g, "-");
    }))

    // render the import target surface
    .then(({imageAreas, setSvgPatternImageAttributes, getId}) => (
        <div className={cx("import-target-surface-k3e", {dimmed})}>
            <div
                className="target-svg-area"

                // set drag handlers for the svg containers - inner svg elements don't fire drag events, so using a
                // workaround that listens for mousedown events on image targets in the SVG, and then sets attributes
                // on the SVG that the following drag events read and use accordingly
                draggable="true"
                onDragStart={e => {

                    // check for a selected image stored on the <svg/> element (initiated from <rect/> mousedown) -
                    // do nothing if not found
                    const selectedImage = e.target["firstChild"].getAttribute("drag-image-path");
                    if (!selectedImage) return false;

                    // set the drag image of the SVG area to match the selected image area
                    const dragImage = dragImageRef.get();
                    dragImage["style"].backgroundImage = "url(\"" + selectedImage + "\")";
                    e.persist(); // will allow the drag image to update after the background completes loading
                    e.dataTransfer.setDragImage(dragImage, dragImage.scrollWidth / 2, dragImage.scrollHeight / 2);

                    // add the image URL to the event
                    e.dataTransfer.setData("img-path-thumbnail", selectedImage);
                    e.dataTransfer.setData("img-path-print", e.target["firstChild"].getAttribute("drag-image-path-print"));
                    e.dataTransfer.setData("img-id", e.target["firstChild"].getAttribute("drag-image-id"));

                }}
                onDragEnd={e => {

                    // gather dragged attributes
                    const selectedSurface = e.target["firstChild"].getAttribute("drag-surface");
                    const selectedSurfaceItem = e.target["firstChild"].getAttribute("drag-surface-item");

                    // return if nothing selected - drop operation will remove these if the image was dragged and dropped from the same dropzone
                    if (!selectedSurface || !selectedSurfaceItem) return;

                    // remove the image from the area the image was dragged from
                    onApplyImage(selectedSurface, selectedSurfaceItem);

                    // remove all drag attributes
                    e.target["firstChild"].removeAttribute("drag-image-id");
                    e.target["firstChild"].removeAttribute("drag-image-path");
                    e.target["firstChild"].removeAttribute("drag-image-path-print");
                    e.target["firstChild"].removeAttribute("drag-surface");
                    e.target["firstChild"].removeAttribute("drag-surface-item");

                }}
            >
                <svg viewBox={"0 0 " + surface.width + " " + surface.height}>
                    <defs>
                        <pattern
                            id={getId("pattern-target-background", surface.name)}
                            width="1"
                            height="1"
                        >
                            <image
                                href={surface.thumbnailHttpPath}
                                x="0"
                                y="0"
                                width={surface.width}
                                height={surface.height}
                            />
                        </pattern>
                        {imageAreas.map((item, index) => (
                            <pattern
                                key={"pattern-target-img-"+index}
                                id={getId("pattern-target-img-"+index, surface.name)}
                                width="1"
                                height="1"
                            >
                                <image {...setSvgPatternImageAttributes(item)}/>
                            </pattern>
                        ))}
                    </defs>
                    <rect
                        id={getId("target-background", surface.name)}
                        className="target-background"
                        x="0"
                        y="0"
                        width={surface.width}
                        height={surface.height}
                        style={{fill: "url(#" + getId("pattern-target-background", surface.name) + ")"}}
                    />
                    {imageAreas.map((item, index) => (
                        <rect key={"target-svg-"+index}
                            id={getId("target-img-"+index, surface.name)}
                            className="target-svg"
                            x={item.left}
                            y={item.top}
                            width={item.width}
                            height={item.height}

                            // set pattern fill only for items containing an image
                            style={item.image && {fill: "url(#" + getId("pattern-target-img-"+index, surface.name) + ")"}}

                            // add states to show area is active for drop target
                            onDragEnter={e => e.target["classList"].add("dragover")}
                            onDragLeave={e => e.target["classList"].remove("dragover")}

                            // prevent default on drag over - drop will not fire if this is not applied
                            onDragOver={e => e.preventDefault()}

                            // add drop handler
                            onDrop={e => {
                                e.preventDefault(); // page redirects if this line is excluded
                                e.target["classList"].remove("dragover");

                                const imageId = +e.dataTransfer.getData("img-id");
                                const printPath = e.dataTransfer.getData("img-path-print");
                                const thumbnailPath = e.dataTransfer.getData("img-path-thumbnail");

                                // if dropping to an image area that already contains the image, make no changes, and remove attributes so that the SVG dragend operation doesn't remove the image from the area
                                if (item.image?.httpPath === thumbnailPath) {
                                    e.target["parentNode"].removeAttribute("drag-image-id");
                                    e.target["parentNode"].removeAttribute("drag-image-path");
                                    e.target["parentNode"].removeAttribute("drag-image-path-print");
                                    e.target["parentNode"].removeAttribute("drag-surface");
                                    e.target["parentNode"].removeAttribute("drag-surface-item");
                                    return;
                                }

                                // load thumbnail to image object so that dimensions can be found
                                const image = new Image();
                                image.src = thumbnailPath;

                                // add the image to the modal state after loading
                                image.onload = () => onApplyImage(surface.name, item.name, imageId, thumbnailPath, printPath, image.width || 100, image.height || 100);
                                // set to use the print image if the thumbnail doesn't load - this happens with SVG images
                                image.onerror = () => {
                                    const printImage = new Image();
                                    printImage.src = printPath;
                                    printImage.onload = () => onApplyImage(surface.name, item.name, imageId, printPath, printPath, image.width || 100, image.height || 100);
                                }

                            }}

                            // add drag operation to pull image from one area to another or to remove - cannot drag
                            // items from within an svg outside of an svg - using the mousedown event on the svg
                            // elements, then a drag from the svg container, can simulate the operation
                            onMouseDown={e => {

                                // reset parent attributes
                                e.target["parentNode"].removeAttribute("drag-image-id");
                                e.target["parentNode"].removeAttribute("drag-image-path");
                                e.target["parentNode"].removeAttribute("drag-image-path-print");
                                e.target["parentNode"].removeAttribute("drag-surface");
                                e.target["parentNode"].removeAttribute("drag-surface-item");

                                // ignore if attempting to drag an item that doesn't contain an image
                                const fill = e.target["style"].fill;
                                if (!fill) {
                                    e.preventDefault();
                                    return;
                                }

                                // find the selected image data to be used by drag operation
                                const patternId = fill.replace(/\"|\(|\)|url/g, "");
                                const imagePath = document.querySelector(patternId + " image").getAttribute("href");
                                const imagePathPrint = document.querySelector(patternId + " image").getAttribute("imageprintpath");
                                const imageId = document.querySelector(patternId + " image").getAttribute("imageid");

                                // assign the path to the <svg/> element to be found by the drag operation - not using
                                // react to prevent unnecessary re-render
                                e.target["parentNode"].setAttribute("drag-image-id", imageId);
                                e.target["parentNode"].setAttribute("drag-image-path", imagePath);
                                e.target["parentNode"].setAttribute("drag-image-path-print", imagePathPrint);
                                e.target["parentNode"].setAttribute("drag-surface", surface.name);
                                e.target["parentNode"].setAttribute("drag-surface-item", item.name);

                            }}

                        />
                    ))}
                </svg>
            </div>
            <h4>{surface.name}</h4>
        </div>
    ))

    .run()

);
